import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IconButton, Tooltip, Typography, CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/client';
import i18next from 'i18next';
import moment from 'moment';

import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { SimpleDatePicker } from 'components/form/FormInputDate';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { DUPLICATE_OFFER_MUTATION, EVICT_OFFER_QUERIES } from './gql';

interface OfferDuplicateButtonProps {
  offerVersionId: number;
  refCode: string;
  companyName?: string | null;
  sourceStartDate?: string | Date | null;
}

/** Prefill arrival: source Anreise when still today or later, otherwise today. */
const resolveDefaultStartDate = (sourceStartDate?: string | Date | null): Date => {
  const today = moment().startOf('day');
  if (!sourceStartDate) {
    return today.toDate();
  }
  const source = moment(sourceStartDate).startOf('day');
  if (source.isBefore(today)) {
    return today.toDate();
  }
  return source.toDate();
};

/**
 * SEM duplicate booking: confirmation dialog with arrival date picker, then navigate to the new offer editor.
 */
export default function OfferDuplicateButton(props: OfferDuplicateButtonProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(() => resolveDefaultStartDate(props.sourceStartDate));

  const minDate = useMemo(() => dayjs().startOf('day'), []);

  const [duplicateOffer, { loading }] = useMutation(DUPLICATE_OFFER_MUTATION);

  const companyLabel = props.companyName || '—';
  const isStartDateValid =
    startDate != null && !moment(startDate).startOf('day').isBefore(moment().startOf('day'));

  const handleOpen = () => {
    setStartDate(resolveDefaultStartDate(props.sourceStartDate));
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!isStartDateValid || !startDate) return;
    try {
      const res = await duplicateOffer({
        variables: {
          offerVersionId: props.offerVersionId,
          startDate: moment(startDate).startOf('day').format('YYYY-MM-DD'),
        },
        update: cache => EVICT_OFFER_QUERIES(cache),
        awaitRefetchQueries: true,
      });
      const result = res.data?.duplicateOffer;
      if (!result) return;
      setDialogOpen(false);
      dispatchMessage(dispatch, i18next.t('offer-duplicate-success', { refCode: result.refCode }));
      navigate(`/offers/${result.offerId}/${result.offerVersionId}#editor`);
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  return (
    <>
      <Tooltip title={i18next.t('offer-duplicate-tooltip')}>
        <IconButton size="small" onClick={handleOpen} disabled={loading} aria-label={String(i18next.t('offer-duplicate-tooltip'))}>
          {loading ? <CircularProgress size={18} /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <ConfirmationDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        title={String(i18next.t('offer-duplicate-title', { refCode: props.refCode, company: companyLabel }))}
        confirmButtonText={String(i18next.t('offer-duplicate-confirm'))}
        onConfirm={handleConfirm}
        keepOpenOnConfirm
        confirmDisabled={!isStartDateValid || loading}
      >
        <Typography sx={{ mb: 2 }}>
          {i18next.t('offer-duplicate-confirm-text', { refCode: props.refCode, company: companyLabel })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {i18next.t('offer-duplicate-startdate-warning')}
        </Typography>
        <SimpleDatePicker
          label={String(i18next.t('offer-duplicate-startdate-label'))}
          value={startDate}
          onChange={setStartDate}
          minDate={minDate}
          disabled={loading}
        />
        {!isStartDateValid && startDate != null && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {i18next.t('offer-duplicate-startdate-past-error')}
          </Typography>
        )}
      </ConfirmationDialog>
    </>
  );
}
